import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import FilterDropdown from '../../components/common/FilterDropdown';
import { GenericView, SearchInput, ActionButtons } from '../../components/common/table';
import ProposalStageForm from './components/ProposalStageForm';
import ViewProposalStage from './components/ViewProposalStage';
import Alert from '../../components/Alert/Alert';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import { getProposalStages, deleteProposalStage, updateProposalStage, createProposalStage } from './api/proposalStageApi';

const ProposalStageManagement = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterData, setFilterData] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedProposalStage, setSelectedProposalStage] = useState(null);
  const [proposalStages, setProposalStages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [alert, setAlert] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  useEffect(() => {
    loadProposalStages();
  }, []);

  const showAlert = (type, message) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 5000); // Auto hide after 5 seconds
  };

  const loadProposalStages = async () => {
    setIsLoading(true);
    try {
      const response = await getProposalStages();
      // Mapping: name -> stageName
      const data = (Array.isArray(response) ? response : response.data || []).map(item => ({
        ...item,
        stageName: item.name // tambahkan mapping ini
      }));
      setProposalStages(data);
    } catch (error) {
      console.error('Error loading proposal stages:', error);
      showAlert('danger', 'Failed to load proposal stages');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (name, value) => {
    setFilterData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFilterReset = () => {
    setFilterData({
      dateFrom: '',
      dateTo: '',
      client: '',
      project: '',
      status: ''
    });
  };

  const handleFilterApply = () => {
    // Add your filter logic here
  };

  const columns = [
    { key: 'stageName', label: 'Stage Name' },
    { key: 'description', label: 'Description' }
  ];

  // Remove workflow-specific dummy data and logic

  const handleFormSubmit = async (formData) => {
    // Ubah payload agar field 'stageName' menjadi 'name'
    const payload = {
      name: formData.stageName,
      description: formData.description
    };
    try {
      if (selectedProposalStage) {
        await updateProposalStage(selectedProposalStage.id, payload);
        showAlert('success', 'Proposal stage updated successfully');
      } else {
        await createProposalStage(payload);
        showAlert('success', 'Proposal stage created successfully');
      }
      await loadProposalStages();
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error saving proposal stage:', error);
      showAlert('danger', 'Failed to save proposal stage');
    }
  };

  // Add these new handler functions
  const handleEdit = (row) => {
    setSelectedProposalStage(row);
    setIsModalOpen(true);
  };

  const handleDelete = (row) => {
    setItemToDelete(row);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteProposalStage(itemToDelete.id);
      await loadProposalStages();
      showAlert('success', 'Proposal stage deleted successfully');
    } catch (error) {
      console.error('Error deleting proposal stage:', error);
      showAlert('danger', 'Failed to delete proposal stage');
    }
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  const handleView = (row) => {
    setSelectedProposalStage(row);
    setIsViewModalOpen(true);
  };

  return (
    <div className="container-xl">
      {/* Alert Component */}
      {alert && (
        <Alert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}

      <div className="row g-2 align-items-center">
        <div className="col">
          <h3 className="page-title m-0">Proposal Stage Management</h3>
        </div>
        <div className="col-auto ms-auto d-print-none">
          <div className="btn-list">
            <button 
              className="btn btn-primary"
              onClick={() => setIsModalOpen(true)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-plus" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                <path d="M12 5l0 14"></path>
                <path d="M5 12l14 0"></path>
              </svg>
              Create
            </button>
          </div>
        </div>
      </div>

      <ProposalStageForm
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedProposalStage(null);
        }}
        onSubmit={handleFormSubmit}
        initialData={selectedProposalStage}
      />

      <ViewProposalStage
        isOpen={isViewModalOpen}
        onClose={() => {
          setIsViewModalOpen(false);
          setSelectedProposalStage(null);
        }}
        data={selectedProposalStage}
      />

      {/* Confirmation Modal for Delete */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setItemToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Proposal Stage"
        message={`Are you sure you want to delete this? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
      />

      <div className="page-body">
        <div className="container-xl">
          <div className="col">
            <div className="card">
              <div className="card-header">
                <ul className="nav nav-tabs card-header-tabs" data-bs-toggle="tabs" role="tablist">
                  <li className="nav-item-tab" role="presentation">
                    <a href="#tabs-list-data" className="nav-link active" data-bs-toggle="tab" aria-selected="true" role="tab">List Data</a>
                  </li>
                </ul>
              </div>
              <div className="card-body">
                <div className="tab-content">
                  <div className="tab-pane active show" id="tabs-list-data" role="tabpanel">
                    <GenericView
                      data={proposalStages}
                      columns={columns}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onView={handleView}
                      isLoading={isLoading}
                      filterComponent={
                        <FilterDropdown
                          filterData={filterData}
                          onFilterChange={handleFilterChange}
                          onReset={handleFilterReset}
                          onApply={handleFilterApply}
                        />
                      }
                      searchComponent={
                        <SearchInput
                          value={searchQuery}
                          onChange={setSearchQuery}
                          placeholder="Search proposal stages..."
                        />
                      }
                      actionButtons={
                        <ActionButtons
                          onExportExcel={() => {
                            showAlert('info', 'Export to Excel functionality coming soon');
                          }}
                          onExportPDF={() => {
                            showAlert('info', 'Export to PDF functionality coming soon');
                          }}
                        />
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProposalStageManagement; 